// The SHAPE of a route (CL13) — a branching graph, not a line.
//
// A route used to be a list, and a list can only be read one way: hop N feeds hop N+1, and the last
// hop is what the caller hears. CL13 gave hops a `from` (`"source"` or an EARLIER hop index) and the
// route an `output_hop`, because a call needs its microphone twice: the STT chain reads it and a VAD
// sidecar reads it, and the sidecar must not become the end of the call by being typed last.
//
// Everything here is PURE — no React, no API — so the editor's validation and the viewer's layout
// are the same functions, tested without a DOM (see routeGraph.test.ts).
//
// The refusals MIRROR `mcpgraph_gateway/streams/route_validator.py` sentence by sentence, for the
// same reason `channelTypes.ts` mirrors the channel check: a client-side guard that disagrees with
// the server is worse than none — it either blocks what would be accepted, or lets through
// something that comes back as a 400 saying something else.

import type { CatalogTool, RouteHop } from '../api/schemas';
import { channelErrorMessage, channelType, typeMismatch } from '../views/channelTypes';

/** The only non-integer `from` there is: "feed me the route's own source". */
export const FROM_SOURCE = 'source';

/** The id of the synthetic node every route starts at (the UP end / the microphone). */
export const SOURCE_ID = 'source';

/** A hop as far as the shape is concerned: which tool, and what it reads. */
export interface ShapeHop {
  tool: string;
  from?: number | string | null;
}

/** Which producer feeds hop `index`: an earlier hop's index, or `null` for the route SOURCE.
 *
 * Absent `from` is today's linear chain — the source for hop 0, `index - 1` for the rest — which is
 * why a route written before CL13 reads identically after it. `undefined` is returned for a `from`
 * that names nothing valid; `validateRouteShape` is what turns that into a sentence. */
export function resolveFrom(index: number, hop: ShapeHop | undefined): number | null | undefined {
  const raw = hop?.from;
  if (raw === undefined || raw === null) return index === 0 ? null : index - 1;
  if (typeof raw === 'string') {
    if (raw.trim().toLowerCase() === FROM_SOURCE) return null;
    // A numeric string is what an <input> hands back; the server coerces nothing, so neither do we
    // beyond the JSON the editor itself writes — the editor always writes a number or "source".
    return undefined;
  }
  if (!Number.isInteger(raw) || raw < 0 || raw >= index) return undefined;
  return raw;
}

/** Which hop is the route's DOWN end: `output_hop`, or the last hop as it always was. */
export function outputIndex(hopCount: number, outputHop?: number | null): number {
  if (outputHop === undefined || outputHop === null) return hopCount - 1;
  return outputHop;
}

// --- validation ------------------------------------------------------------

export type ShapeProblemCode = 'bad_from' | 'bad_output_hop' | 'dangling' | 'empty';

export interface ShapeProblem {
  code: ShapeProblemCode;
  /** `error` blocks the save (the server would refuse it too); `warn` only colours the graph. */
  severity: 'error' | 'warn';
  hop?: number;
  message: string;
}

/** Every structural complaint about a route's shape, in hop order.
 *
 * The two ERRORS are the server's own: a `from` that points forwards, at itself, or at nothing is a
 * cycle (`route_validator.resolve_from`), and an `output_hop` outside the route is a hop that does
 * not exist (`validate_output_hop`).
 *
 * The WARNING has no server equivalent on purpose: a terminal branch is exactly what a VAD sidecar
 * is, so a route full of them is legal. It is still the one shape a user creates by ACCIDENT —
 * appending a hop and forgetting that the output stayed where it was — so it is drawn, not
 * refused. */
export function validateRouteShape(hops: ShapeHop[], outputHop?: number | null): ShapeProblem[] {
  const problems: ShapeProblem[] = [];
  if (!hops.length) {
    return [{ code: 'empty', severity: 'error', message: 'a route needs at least one hop' }];
  }
  hops.forEach((hop, i) => {
    const raw = hop?.from;
    if (raw === undefined || raw === null) return;
    if (resolveFrom(i, hop) !== undefined) return;
    problems.push({
      code: 'bad_from',
      severity: 'error',
      hop: i,
      message:
        typeof raw === 'number' && Number.isInteger(raw)
          ? `hop ${i}: 'from' must name an EARLIER hop (0..${i - 1}) or '${FROM_SOURCE}', not ${raw}`
          : `hop ${i}: 'from' must be '${FROM_SOURCE}' or the index of an earlier hop, not ${JSON.stringify(raw)}`,
    });
  });
  if (outputHop !== undefined && outputHop !== null) {
    if (!Number.isInteger(outputHop)) {
      problems.push({
        code: 'bad_output_hop',
        severity: 'error',
        message: `output_hop must be a hop index, not ${JSON.stringify(outputHop)}`,
      });
    } else if (outputHop < 0 || outputHop >= hops.length) {
      problems.push({
        code: 'bad_output_hop',
        severity: 'error',
        message: `output_hop ${outputHop} is out of range (the route has ${hops.length} hops)`,
      });
    }
  }
  const out = outputIndex(hops.length, outputHop);
  const consumed = new Set<number>();
  hops.forEach((hop, i) => {
    const src = resolveFrom(i, hop);
    if (typeof src === 'number') consumed.add(src);
  });
  hops.forEach((hop, i) => {
    if (i === out || consumed.has(i)) return;
    problems.push({
      code: 'dangling',
      severity: 'warn',
      hop: i,
      message:
        `hop ${i} (${hop?.tool || '—'}) is a TAP: nothing downstream reads it and it is not the ` +
        `route output. Its frames reach the opener as a side branch — intended for a sidecar, ` +
        `an accident for anything else.`,
    });
  });
  return problems;
}

// --- the graph -------------------------------------------------------------

export interface RouteGraphNode {
  id: string;
  kind: 'source' | 'hop';
  /** Hop index; `-1` for the source node. */
  index: number;
  label: string;
  /** Distance from the source in hops — the graph's column. */
  depth: number;
  /** Row within the column, assigned in hop order so a branch sits BESIDE the chain. */
  lane: number;
  /** This hop is what the caller listens to (the route's DOWN end). */
  isOutput: boolean;
  /** Terminal branch: neither the output nor read by another hop — a tap on `:open`. */
  isTap: boolean;
  /** Its `from` names nothing valid, so it is wired to nothing. */
  broken: boolean;
}

export interface RouteGraphEdge {
  from: string;
  to: string;
  /** The edge feeding a tap, drawn apart from the spine of the call. */
  toTap: boolean;
}

export interface RouteGraphModel {
  nodes: RouteGraphNode[];
  edges: RouteGraphEdge[];
  outputIndex: number;
  /** How wide the widest column is — a linear route is 1, and stays drawn as a line. */
  lanes: number;
  depth: number;
}

export const hopNodeId = (index: number) => `hop-${index}`;

/** Lay a route out as a left-to-right graph: source, then each hop one column past its producer.
 *
 * A hop whose `from` is broken is placed in column 0 beside the source rather than dropped — a hop
 * you cannot see is a hop you cannot fix. */
export function buildRouteGraph(hops: ShapeHop[], outputHop?: number | null): RouteGraphModel {
  const out = outputIndex(hops.length, outputHop);
  const consumed = new Set<number>();
  const producer: (number | null | undefined)[] = hops.map((h, i) => {
    const src = resolveFrom(i, h);
    if (typeof src === 'number') consumed.add(src);
    return src;
  });

  const depths = new Map<string, number>([[SOURCE_ID, 0]]);
  const nodes: RouteGraphNode[] = [
    {
      id: SOURCE_ID,
      kind: 'source',
      index: -1,
      label: 'source',
      depth: 0,
      lane: 0,
      isOutput: false,
      isTap: false,
      broken: false,
    },
  ];
  const edges: RouteGraphEdge[] = [];

  hops.forEach((hop, i) => {
    const src = producer[i];
    const broken = src === undefined;
    const parentId = typeof src === 'number' ? hopNodeId(src) : SOURCE_ID;
    const parentDepth = broken ? 0 : (depths.get(parentId) ?? 0);
    const depth = parentDepth + 1;
    depths.set(hopNodeId(i), depth);
    const isTap = i !== out && !consumed.has(i);
    nodes.push({
      id: hopNodeId(i),
      kind: 'hop',
      index: i,
      label: hop?.tool || '—',
      depth,
      lane: 0,
      isOutput: i === out,
      isTap,
      broken,
    });
    if (!broken) edges.push({ from: parentId, to: hopNodeId(i), toTap: isTap });
  });

  // Lanes: hop order within a column. A linear route keeps every node on lane 0 and so is drawn
  // exactly as the line it was before branches existed.
  const perDepth = new Map<number, number>();
  for (const n of nodes) {
    const used = perDepth.get(n.depth) ?? 0;
    n.lane = used;
    perDepth.set(n.depth, used + 1);
  }

  return {
    nodes,
    edges,
    outputIndex: out,
    lanes: Math.max(1, ...perDepth.values()),
    depth: Math.max(0, ...nodes.map((n) => n.depth)),
  };
}

// --- editing a shape -------------------------------------------------------

/** Write the producer of every hop down EXPLICITLY, exactly as the server would compute it.
 *
 * The implicit chain is positional, so any edit that moves a hop silently re-points its neighbours.
 * Materialising first means an edit changes only what the user touched. */
export function materialize<T extends ShapeHop>(hops: T[]): T[] {
  return hops.map((h, i) => {
    const src = resolveFrom(i, h);
    return { ...h, from: src === null ? FROM_SOURCE : src === undefined ? h.from : src };
  });
}

export interface ShapeEdit<T extends ShapeHop> {
  hops: T[];
  outputHop: number | null;
}

/** Remove hop `i`, keeping every remaining hop pointed at the producer it MEANT to read.
 *
 * A hop that read the removed one inherits the removed one's producer — a deleted link in a chain
 * closes the chain, which is what someone deleting a middle hop is asking for. Indices above the
 * hole shift down, and so does `output_hop`; deleting the output hop hands the end of the route
 * back to its default (the last hop), because the alternative is a route whose DOWN end points at
 * a hop that no longer exists. */
export function removeHop<T extends ShapeHop>(
  hops: T[],
  outputHop: number | null,
  i: number,
): ShapeEdit<T> {
  const explicit = materialize(hops);
  const removedFrom = explicit[i]?.from;
  const shift = (v: number | string | null | undefined): number | string | null | undefined => {
    if (typeof v !== 'number') return v;
    if (v === i) return removedFrom === undefined ? FROM_SOURCE : removedFrom;
    return v > i ? v - 1 : v;
  };
  const next = explicit
    .filter((_, j) => j !== i)
    .map((h, j) => {
      const from = shift(h.from);
      // A producer that is no longer EARLIER than its consumer cannot stay: the server refuses it,
      // and the route source is the only end that is always upstream of everything.
      const safe = typeof from === 'number' && from >= j ? FROM_SOURCE : from;
      return { ...h, from: safe } as T;
    });
  const out =
    outputHop === null || outputHop === undefined
      ? null
      : outputHop === i
        ? null
        : outputHop > i
          ? outputHop - 1
          : outputHop;
  return { hops: next, outputHop: next.length ? out : null };
}

/** Make hop `i` a TAP by moving the route's DOWN end off it.
 *
 * `is_tap` is not a field — the server derives it from the shape ("terminal and not the output"),
 * so the only way to declare a sidecar is to say which hop the caller listens to INSTEAD. The end
 * moves to the last other hop, which is where it sat before the sidecar was appended. */
export function markAsTap(hops: ShapeHop[], outputHop: number | null, i: number): number | null {
  const out = outputIndex(hops.length, outputHop);
  if (out !== i) return outputHop;
  for (let j = hops.length - 1; j >= 0; j--) if (j !== i) return j;
  return outputHop;
}

/** The shape of a LIVE instance when its route definition cannot be read — a fallback, not a truth.
 *
 * An instance carries hop indices and `is_tap`, but not the `from` each hop was wired with, so the
 * chain here is the positional one and every tap is drawn hanging off the source. That is the shape
 * a sidecar has in practice and it is still a GUESS: the declared route is preferred wherever it can
 * be fetched, and only a version that has been deleted falls through to this.
 *
 * Gaps matter: a branch skipped for want of a provider leaves a hole in the indices, and filling by
 * POSITION would relabel every hop after it. */
export function shapeFromInstance(
  hops: { index: number; canonical_tool: string; is_tap?: boolean }[],
  skipped: { hop: number; tool?: string }[] = [],
): { hops: ShapeHop[]; outputHop: number | null } {
  const top = Math.max(-1, ...hops.map((h) => h.index), ...skipped.map((s) => s.hop));
  const shape: ShapeHop[] = [];
  const taps = new Set<number>();
  for (let i = 0; i <= top; i++) {
    const live = hops.find((h) => h.index === i);
    const gone = skipped.find((s) => s.hop === i);
    if (live?.is_tap || gone) taps.add(i);
    shape.push({
      tool: live?.canonical_tool ?? gone?.tool ?? '—',
      from: live?.is_tap || gone ? FROM_SOURCE : undefined,
    });
  }
  let out: number | null = null;
  for (let i = top; i >= 0; i--) if (!taps.has(i)) { out = i; break; }
  return { hops: shape, outputHop: out };
}

// --- channel compatibility across the real edges ---------------------------

export interface EdgeMismatch {
  from: number | null; // hop index, or null for the source
  to: number;
  out?: string;
  in?: string;
  message: string;
  /** `error` blocks the save; `unknown` only says so — see `firstIncompatibleEdge`. */
  severity: 'error' | 'unknown';
}

/** Does the catalog say anything at all about this tool's channels?
 *
 * `GET /tools` returns no `channels` field on any entry (the gateway's `ToolCatalogEntry` has none),
 * so on a real stand the client mirror has NO INFORMATION — and answering "declares no input channel
 * type" to that is the client asserting something it cannot know. It reads identically to a tool
 * that really declared nothing, and it disabled Save on every multi-hop route on cubi, including the
 * call route the gateway itself had just accepted. */
function catalogKnowsChannels(t: CatalogTool | undefined): boolean {
  return !!t && Array.isArray(t.channels) && t.channels.length > 0;
}

/** The first producer→consumer pair whose channel types cannot meet.
 *
 * Edge-based, not position-based: on a branching route hop N does not necessarily feed hop N+1, and
 * comparing them anyway both invents mismatches (the VAD's output against the TTS's input, two hops
 * that never touch) and misses real ones (a branch against the source it actually reads). */
export function firstIncompatibleEdge(
  hops: RouteHop[] | ShapeHop[],
  byCanonical: (sel: string) => CatalogTool | undefined,
): EdgeMismatch | null {
  for (let i = 0; i < hops.length; i++) {
    const src = resolveFrom(i, hops[i]);
    // The SOURCE's media type is declared by the ends, not by a tool — nothing to compare against
    // here, and a broken `from` is already a structural error with its own sentence.
    if (src === null || src === undefined) continue;
    const producer = byCanonical(hops[src].tool);
    const consumer = byCanonical(hops[i].tool);
    if (!catalogKnowsChannels(producer) || !catalogKnowsChannels(consumer)) {
      // Said, not enforced: the server holds the tool definitions and refuses a chain it cannot
      // check, so nothing unsafe gets through — the editor simply stops claiming to have checked.
      const silent = !catalogKnowsChannels(producer) ? { hop: src, tool: hops[src].tool } : { hop: i, tool: hops[i].tool };
      return {
        from: src,
        to: i,
        severity: 'unknown',
        message:
          `the catalog carries no channel declarations for hop ${silent.hop} (${silent.tool}), so ` +
          `this chain cannot be checked here — the gateway validates it on save`,
      };
    }
    const a = {
      index: src,
      selector: hops[src].tool,
      outType: channelType(byCanonical(hops[src].tool), 'DOWN'),
      inType: channelType(byCanonical(hops[src].tool), 'UP'),
    };
    const b = {
      index: i,
      selector: hops[i].tool,
      outType: channelType(byCanonical(hops[i].tool), 'DOWN'),
      inType: channelType(byCanonical(hops[i].tool), 'UP'),
    };
    const problem = typeMismatch(a.outType, b.inType);
    if (problem) {
      return {
        from: src,
        to: i,
        out: a.outType,
        in: b.inType,
        severity: 'error',
        message: channelErrorMessage(a, b, problem),
      };
    }
  }
  return null;
}
